import React from 'react';
import { Box, Paper, Stack, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import { Build } from '@mui/icons-material';
import { grey } from '@mui/material/colors';
import { formatCurrency } from 'utils';
import { NO_DATA } from 'constants/common';
import { calcDiscount } from 'modules/equipment/lib/charges';

import { EquipmentAttachment, EquipmentDetailsEquipment } from './EquipmentDetailsTypes';

interface AttachmentsTabProps {
  attachments: EquipmentAttachment[];
  equipment: EquipmentDetailsEquipment | undefined;
}

export const AttachmentsTab = ({ attachments, equipment }: AttachmentsTabProps) => {
  const priceRows: Array<{ label: string; value: string }> = [
    {
      label: 'Wholesale Cost',
      value: equipment?.wholesaleCost != null ? formatCurrency(equipment.wholesaleCost, true) : NO_DATA,
    },
    {
      label: 'Retail Price',
      value: equipment?.retailPrice != null ? formatCurrency(equipment.retailPrice, true) : NO_DATA,
    },
    {
      label: 'Final Customer Price',
      value:
        equipment?.retailPrice != null && equipment?.discountType
          ? formatCurrency(
              calcDiscount(equipment.retailPrice, equipment.discountValue ?? 0, equipment.discountType as any),
              true
            )
          : NO_DATA,
    },
  ];

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} gap={3} mt={2}>
      {/* Left Column: Attachments */}
      <Paper variant="outlined" sx={{ flex: 1, minWidth: 0, p: 2 }}>
        <Stack direction="row" alignItems="center" gap={1} mb={1}>
          <Build fontSize="small" color="action" />
          <Typography variant="subtitle1" fontWeight={600} sx={{ textTransform: 'none' }}>
            Attachment
          </Typography>
        </Stack>

        {attachments.length > 0 ? (
          <Stack gap={2} divider={<Box sx={{ borderBottom: `1px solid ${grey[300]}` }} />}>
            {attachments.map((attachment, index) => (
              <Box key={index}>
                <Typography variant="subtitle2" fontWeight={600} mb={0.5}>
                  #{index + 1}
                </Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow sx={{ '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell sx={{ color: grey[600], border: 'none', pl: 0, py: 0.75, width: '45%' }}>
                        Qty. per asset
                      </TableCell>
                      <TableCell sx={{ border: 'none', py: 0.75 }}>{attachment.quantityPerAsset ?? NO_DATA}</TableCell>
                    </TableRow>
                    <TableRow sx={{ '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell sx={{ color: grey[600], border: 'none', pl: 0, py: 0.75, width: '45%' }}>
                        Type
                      </TableCell>
                      <TableCell sx={{ border: 'none', py: 0.75 }}>{attachment.type || NO_DATA}</TableCell>
                    </TableRow>
                    <TableRow sx={{ '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell sx={{ color: grey[600], border: 'none', pl: 0, py: 0.75, width: '45%' }}>
                        Manufacturer
                      </TableCell>
                      <TableCell sx={{ border: 'none', py: 0.75 }}>{attachment.manufacturer || NO_DATA}</TableCell>
                    </TableRow>
                    <TableRow sx={{ '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell sx={{ color: grey[600], border: 'none', pl: 0, py: 0.75, width: '45%' }}>
                        Model
                      </TableCell>
                      <TableCell sx={{ border: 'none', py: 0.75 }}>{attachment.model || NO_DATA}</TableCell>
                    </TableRow>
                    <TableRow sx={{ '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell sx={{ color: grey[600], border: 'none', pl: 0, py: 0.75, width: '45%' }}>
                        Category
                      </TableCell>
                      <TableCell sx={{ border: 'none', py: 0.75 }}>{attachment.category || NO_DATA}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="textSecondary">
            No attachments available.
          </Typography>
        )}
      </Paper>

      {/* Right Column: Price */}
      <Paper variant="outlined" sx={{ flex: 1, minWidth: 0, p: 2, alignSelf: 'flex-start' }}>
        <Stack direction="row" alignItems="center" gap={1} mb={1}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ textTransform: 'none' }}>
            $ Price
          </Typography>
        </Stack>
        <Table size="small">
          <TableBody>
            {priceRows.map(row => (
              <TableRow key={row.label} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                <TableCell sx={{ color: grey[600], border: 'none', pl: 0, py: 0.75, width: '45%' }}>
                  {row.label}
                </TableCell>
                <TableCell sx={{ border: 'none', py: 0.75 }}>{row.value}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Stack>
  );
};
